import { useRef, useState, useEffect, useCallback } from 'react'
import type { Api } from '@reduxjs/toolkit/query/react'

import Widget from './Widget'
import { WidgetContextProvider } from '../../context/WidgetContext'
import { PopupBtn } from '../common/button/PopupBtn'
import { EntityStatus } from '../../store/model/entityStatus.model'

import { useGetAgentConfig } from '../../hook/useGetAgentConfig'
import { LibConfig } from '../../types/lib-config'
import { setCustomColor } from '../../helpers/setCustomThemeColor'
import { PopupMessage } from '../common/block/PopupMessage'
import { normalizeAgentWidgetMode } from '../../types/agent-mode'

type AnyApi = Api<any, any, any, any, any>

type WidgetContainerProps = {
  config: LibConfig
  agentApi: AnyApi
  chatApi: AnyApi
  modeApi: AnyApi
}

export const WidgetContainer = ({
  config,
  agentApi,
  chatApi,
  modeApi,
}: WidgetContainerProps) => {
  const { agent } = useGetAgentConfig(config, agentApi)
  const mode = normalizeAgentWidgetMode(agent.mode, 'chat')

  const parent = useRef<HTMLDivElement>(null)
  const maxWidth = 1000

  const [isSmallBody, setIsSmallBody] = useState(false)
  const [bodyWidth, setBodyWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : maxWidth
  )

  const measure = useCallback(() => {
    if (typeof window === 'undefined') return

    const nextWidth =
      parent.current?.closest('body')?.clientWidth ??
      document.body?.clientWidth ??
      window.innerWidth

    setBodyWidth((prev) => (prev === nextWidth ? prev : nextWidth))
    setIsSmallBody((prev) => {
      const nextIsSmall = nextWidth < maxWidth
      return prev === nextIsSmall ? prev : nextIsSmall
    })
  }, [maxWidth])

  useEffect(() => {
    measure() // initial

    // Window resize (covers most cases)
    window.addEventListener('resize', measure, { passive: true })

    // Optional: also respond to body/layout changes not caused by window resize
    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure())
      if (document.body) ro.observe(document.body)
    }

    return () => {
      window.removeEventListener('resize', measure)
      if (ro) ro.disconnect()
    }
  }, [measure])

  useEffect(() => {
    setCustomColor(agent.customColor || '', parent.current)
  }, [agent.customColor])

  if (!agent || agent.status !== EntityStatus.live) return <></>

  return (
    <div ref={parent} className='osirus-agent'>
      <WidgetContextProvider
        agent={agent}
        agentApi={agentApi}
        chatApi={chatApi}
        modeApi={modeApi}
        mode={mode}
        config={config}
      >
        <PopupBtn />
        <Widget isSmall={isSmallBody} bodyWidth={bodyWidth} />
        {mode !== 'search' && agent.greeting && <PopupMessage message={agent.greeting} />}
      </WidgetContextProvider>
    </div>
  )
}
